import { Suspense, lazy } from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import AuthProvider from '@/components/AuthProvider';
import AuthGuard from '@/components/AuthGuard';
import AppSidebar from '@/components/layout/AppSidebar';

// Pages outside AuthProvider (Excel add-in runs its own NAA-aware MSAL;
// excel-functions is a headless custom-functions runtime — neither should
// share the main app's MsalProvider instance).
const ExcelPage = lazy(() => import('./pages/excel/ExcelPage'));
const ExcelFunctionsPage = lazy(() => import('./pages/excel-functions/ExcelFunctionsPage'));

// Authenticated pages
const WorkbenchPage = lazy(() => import('./pages/workbench/WorkbenchPage'));
const LoginPage = lazy(() => import('./pages/login/LoginPage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const AgentAssistPage = lazy(() => import('./pages/agent-assist/AgentAssistPage'));
const AgentAssistAdminPage = lazy(() => import('./pages/agent-assist-admin/AgentAssistAdminPage'));
const AnalyzePage = lazy(() => import('./pages/analyze/AnalyzePage'));
const AskClaudePage = lazy(() => import('./pages/ask-claude/AskClaudePage'));
const ConversationPage = lazy(() => import('./pages/conversation/ConversationPage'));
const CrossSellPage = lazy(() => import('./pages/crosssell/CrossSellPage'));
const CrossSellComparePage = lazy(() => import('./pages/crosssell/CrossSellComparePage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const BusinessPerformancePage = lazy(() => import('./pages/dashboard/business-performance/BusinessPerformancePage'));
const RevenueBridgePage = lazy(() => import('./pages/bridge/RevenueBridgePage'));
const PendingBridgePage = lazy(() => import('./pages/bridge/PendingBridgePage'));
const ChurnRiskPage = lazy(() => import('./pages/churn/ChurnRiskPage'));
const DataPage = lazy(() => import('./pages/data/DataPage'));
const GeoPage = lazy(() => import('./pages/geo/GeoPage'));
const WfmPage = lazy(() => import('./pages/wfm/WfmPage'));

function Loading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</div>
    </div>
  );
}

/** The /login route lives inside AuthProvider (it calls useMsal) but
 *  outside AuthGuard (otherwise unauthenticated users would never get
 *  there). */
function PublicAuthLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

/** Every authenticated page renders inside the shared sidebar shell.
 *  AuthGuard handles login redirect, pending-user state, and role-based
 *  route gating — individual pages no longer need to re-implement these. */
function AuthLayout() {
  return (
    <AuthProvider>
      <AuthGuard>
        <div className="flex h-screen w-screen overflow-hidden">
          <AppSidebar />
          <Outlet />
        </div>
      </AuthGuard>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Routes that bypass AuthProvider */}
        <Route path="/excel" element={<ExcelPage />} />
        <Route path="/excel-functions" element={<ExcelFunctionsPage />} />

        {/* Login lives in its own MSAL-only layout (no AuthGuard) */}
        <Route element={<PublicAuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Authenticated routes — wrapped in AuthGuard + shared sidebar */}
        <Route element={<AuthLayout />}>
          <Route path="/" element={<WorkbenchPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/agent-assist" element={<AgentAssistPage />} />
          <Route path="/agent-assist-admin" element={<AgentAssistAdminPage />} />
          <Route path="/analyze" element={<AnalyzePage />} />
          <Route path="/ask-claude" element={<AskClaudePage />} />
          <Route path="/conversation" element={<ConversationPage />} />
          <Route path="/crosssell" element={<CrossSellPage />} />
          <Route path="/crosssell-v3" element={<CrossSellPage version="v3" />} />
          <Route path="/crosssell-v4" element={<CrossSellPage version="v4" />} />
          <Route path="/crosssell/compare" element={<CrossSellComparePage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/dashboard/business-performance" element={<BusinessPerformancePage />} />
          <Route path="/dashboard/revenue-bridge" element={<RevenueBridgePage />} />
          <Route path="/dashboard/pending-bridge" element={<PendingBridgePage />} />
          <Route path="/dashboard/churn" element={<ChurnRiskPage />} />
          <Route path="/data" element={<DataPage />} />
          <Route path="/geo" element={<GeoPage />} />
          <Route path="/wfm" element={<WfmPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
